import { BRAND } from "./brand";

/*
 * What the build's pre-rendering (client/src/entry-server.tsx) writes around each page: the page head,
 * robots.txt, the sitemap and the Static Web Apps routes.
 */

const escapeHtml = (s: string) =>
  s.replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

/** A page's title: its own before the store's name, or the store's site title. */
export const pageTitle = (title?: string) => (title ? `${title} | ${BRAND.name}` : BRAND.site.title);

/** JSON for a `<script>` data block: `<` escaped, so no text in it can close the block. */
export const scriptJson = (value: unknown) => JSON.stringify(value).replace(/</g, "\\u003c");

/** The store as search engines read it (schema.org), from brand.json. */
export function localBusinessData(origin: string) {
  const days = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
  const { line1, city, state, postalCode } = BRAND.address;
  return {
    "@context": "https://schema.org",
    "@type": "HardwareStore",
    name: BRAND.name,
    url: `${origin}/`,
    logo: `${origin}/brand/${encodeURI(BRAND.logo.file)}`,
    telephone: BRAND.phone,
    address: {
      "@type": "PostalAddress",
      streetAddress: line1,
      addressLocality: city,
      addressRegion: state,
      postalCode,
      addressCountry: "US",
    },
    openingHoursSpecification: BRAND.openHours.flatMap((hours, day) =>
      hours
        ? [{ "@type": "OpeningHoursSpecification", dayOfWeek: days[day], opens: hours.open, closes: hours.close }]
        : [],
    ),
    sameAs: Object.values(BRAND.social).filter(Boolean),
  };
}

export interface PageHead {
  path: string;
  title?: string;
  description?: string;
  /** May search engines list this build's pages? */
  indexable: boolean;
  /** The site's address (https://host), when this build knows it: production's. */
  origin?: string;
  /** Add the store's data: the pages about the store itself. */
  storeData?: boolean;
}

/** The tags a pre-rendered page adds to the head: robots or canonical, social tags and store data. */
export function headTags({ path, title, description, indexable, origin, storeData }: PageHead): string {
  const url = origin && `${origin}${path}`;
  // An image at an absolute address, in a format social sites read (not SVG).
  const image = origin && !BRAND.logo.file.endsWith(".svg") && `${origin}/brand/${encodeURI(BRAND.logo.file)}`;
  const property = (name: string, content: string) => `<meta property="${name}" content="${escapeHtml(content)}" />`;
  const tags = [
    indexable && url
      ? `<link rel="canonical" href="${escapeHtml(url)}" />`
      : `<meta name="robots" content="noindex" />`,
    property("og:type", "website"),
    property("og:site_name", BRAND.name),
    property("og:title", pageTitle(title)),
    property("og:description", description ?? BRAND.site.description),
    url && property("og:url", url),
    image && property("og:image", image),
    indexable &&
      storeData &&
      origin &&
      `<script type="application/ld+json">${scriptJson(localBusinessData(origin))}</script>`,
  ];
  return tags.filter(Boolean).join("\n    ");
}

/** The page template with this page's title, description and extra head tags. */
export function withHead(template: string, head: { title?: string; description?: string; tags: string }): string {
  return template
    .replace(/<title>[^<]*<\/title>/, () => `<title>${escapeHtml(pageTitle(head.title))}</title>`)
    .replace(
      /<meta name="description" content="[^"]*" \/>/,
      () => `<meta name="description" content="${escapeHtml(head.description ?? BRAND.site.description)}" />`,
    )
    .replace("</head>", () => `    ${head.tags}\n  </head>`);
}

/** Pages with nothing for search results, and the pre-rendered files' own addresses (their pages have better ones). */
const NOT_FOR_RESULTS = [
  "/account",
  "/admin",
  "/cart",
  "/checkout",
  "/contractor/",
  "/order/",
  "/search",
  "/prerendered/",
];

/**
 * robots.txt. A site that isn't indexable still lets crawlers in: it's the noindex tag that keeps its
 * pages out of results, and a crawler kept out never reads it. /api stays open on an indexable site too,
 * since a crawler that runs JavaScript needs it to see a shopping page.
 */
export const robotsTxt = (origin: string | undefined, indexable: boolean) =>
  indexable && origin
    ? `User-agent: *\nAllow: /\n${NOT_FOR_RESULTS.map((p) => `Disallow: ${p}\n`).join("")}\nSitemap: ${origin}/sitemap.xml\n`
    : `User-agent: *\nAllow: /\n`;

export const sitemapXml = (origin: string, paths: string[]) =>
  `<?xml version="1.0" encoding="UTF-8"?>\n<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">\n${paths
    .map((p) => `  <url><loc>${escapeHtml(`${origin}${p}`)}</loc></url>`)
    .join("\n")}\n</urlset>\n`;

/** A browser's route as Static Web Apps routes: wildcards only go last, so `/a/:b/c` covers all of `/a/*`. */
export function hostRoutes(path: string): string[] {
  const param = path.indexOf("/:");
  return param === -1 ? [path] : [`${path.slice(0, param)}/*`];
}

export interface HostRoute {
  route: string;
  rewrite?: string;
  headers?: Record<string, string>;
}

/**
 * The Static Web Apps routes that replace a catch-all fallback: each pre-rendered page to its file, each
 * other route of the app to the empty shell. Everything else is a real 404. Revalidated, because each
 * names the build's hashed assets.
 */
export function staticWebAppRoutes(pages: { path: string; file: string }[], appRoutes: string[]): HostRoute[] {
  const headers = { "Cache-Control": "no-cache" };
  const own = new Set(pages.map((p) => p.path));
  const all = [...new Set(appRoutes.filter((r) => r !== "*" && !own.has(r)).flatMap(hostRoutes))];
  // Each goes to the shell, so a route under a wildcard adds nothing, and the host warns about it.
  const wildcards = all.filter((r) => r.endsWith("/*")).map((r) => r.slice(0, -1));
  const shell = all.filter((r) => r.endsWith("/*") || !wildcards.some((w) => r.startsWith(w)));
  return [
    ...pages.map(({ path, file }) =>
      path === "/" ? { route: path, headers } : { route: path, rewrite: `/${file}`, headers },
    ),
    ...shell.map((route) => ({ route, rewrite: "/shell.html", headers })),
    { route: "/404.html", headers },
    { route: "/shell.html", headers },
  ];
}
